import type React from 'react';
import './MembershipTypePointsSection.css';
import { Wallet, Star, Target } from 'lucide-react';

type MembershipTypePointsSectionProps = {
  values: {
    pointsEnabled: boolean;
    pointsEarnRatePaisePerPoint: number;
    pointsRedeemPaisePerPoint: number;
    pointsMinRedeem: number;
    pointsExpiryDays: number;
  };
  onChange: (key: string, value: string | number | boolean) => void;
  errors: Record<string, string>;
};

const MembershipTypePointsSection = ({ values, onChange, errors }: MembershipTypePointsSectionProps) => {
  const isEnabled = Boolean(values.pointsEnabled);

  // Pass empty string up when the user clears the input so the field can
  // actually render blank between keystrokes. `Number('' || 0) === 0` was
  // forcing the field to repopulate with `0` the moment the user backspaced
  // past the last digit, making it impossible to type a fresh value.
  const numericChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(key, '');
      return;
    }
    const parsed = Number(raw);
    onChange(key, Number.isNaN(parsed) ? '' : parsed);
  };

  const earnRate = Number(values.pointsEarnRatePaisePerPoint || 0);
  const redeemRate = Number(values.pointsRedeemPaisePerPoint || 0);

  // Simulator values
  const monthlySpend = 5000; // ₹5,000
  const monthlySpendPaise = monthlySpend * 100;
  const monthlyPoints = earnRate > 0 ? Math.floor(monthlySpendPaise / earnRate) : 0;
  const monthlyRedeemValue = redeemRate > 0 ? (monthlyPoints * redeemRate) / 100 : 0;

  return (
    <section className={`appointments-v2-card membership-points-card${isEnabled ? '' : ' is-disabled'}`}>
      <div className="appointments-v2-card-header membership-points-card__header">
        <div>
          <h3>Points</h3>
          <p className="membership-points-card__intro">Decide whether this tier runs on loyalty points and how members earn or redeem them.</p>
        </div>
      </div>

      <div className="appointments-v2-card-body membership-points-card__body">
        <div className="membership-points-card__toggleRow">
          <div className="membership-points-card__toggleCopy">
            <span className="membership-points-card__toggleLabel">Enable points earning</span>
            <p>When enabled, customers earn points on every transaction and can convert them into value later.</p>
          </div>
          <button
            type="button"
            className={`membership-points-card__switch${isEnabled ? ' enabled' : ''}`}
            role="switch"
            aria-checked={isEnabled}
            aria-label="Enable points earning"
            onClick={() => onChange('pointsEnabled', !isEnabled)}
          >
            <span className="membership-points-card__switchThumb" aria-hidden="true" />
          </button>
        </div>

        {isEnabled ? (
          <>
            {/* Visual formula */}
            <div className="membership-points-card__formula">
              <div className="membership-points-card__formula-step">
                <span className="membership-points-card__formula-icon"><Wallet size={16} /></span>
                <span>Customer spends ₹{earnRate > 0 ? (earnRate / 100).toLocaleString('en-IN') : '...'}</span>
              </div>
              <span className="membership-points-card__formula-arrow">→</span>
              <div className="membership-points-card__formula-step">
                <span className="membership-points-card__formula-icon"><Star size={16} /></span>
                <span>Earns 1 point</span>
              </div>
              <span className="membership-points-card__formula-arrow">→</span>
              <div className="membership-points-card__formula-step">
                <span className="membership-points-card__formula-icon"><Target size={16} /></span>
                <span>Worth ₹{redeemRate > 0 ? (redeemRate / 100).toLocaleString('en-IN') : '...'}</span>
              </div>
            </div>

            <div className="membership-points-card__configGrid">
              <label className="field membership-points-card__field">
                <span className="label membership-points-card__label">Earn rate (paise per point)</span>
                <input
                  type="number"
                  min={1}
                  className="input membership-points-card__input"
                  value={values.pointsEarnRatePaisePerPoint}
                  onChange={numericChange('pointsEarnRatePaisePerPoint')}
                  placeholder="100"
                />
                <span className="membership-points-card__helper">How much a customer spends (in paise) to earn 1 point. 100 = ₹1 per point.</span>
                {errors.pointsEarnRatePaisePerPoint ? <span className="membership-points-card__error">{errors.pointsEarnRatePaisePerPoint}</span> : null}
              </label>

              <label className="field membership-points-card__field">
                <span className="label membership-points-card__label">Redeem rate (paise per point)</span>
                <input
                  type="number"
                  min={1}
                  className="input membership-points-card__input"
                  value={values.pointsRedeemPaisePerPoint}
                  onChange={numericChange('pointsRedeemPaisePerPoint')}
                  placeholder="100"
                />
                <span className="membership-points-card__helper">How much 1 point is worth (in paise) when redeemed. 100 = ₹1 per point.</span>
                {errors.pointsRedeemPaisePerPoint ? <span className="membership-points-card__error">{errors.pointsRedeemPaisePerPoint}</span> : null}
              </label>

              <label className="field membership-points-card__field">
                <span className="label membership-points-card__label">Minimum redeem points</span>
                <input
                  type="number"
                  min={0}
                  className="input membership-points-card__input"
                  value={values.pointsMinRedeem}
                  onChange={numericChange('pointsMinRedeem')}
                  placeholder="0"
                />
                <span className="membership-points-card__helper">Minimum balance needed before a member can redeem points.</span>
                {errors.pointsMinRedeem ? <span className="membership-points-card__error">{errors.pointsMinRedeem}</span> : null}
              </label>

              <label className="field membership-points-card__field">
                <span className="label membership-points-card__label">Points expiry (days)</span>
                <input
                  type="number"
                  min={0}
                  className="input membership-points-card__input"
                  value={values.pointsExpiryDays}
                  onChange={numericChange('pointsExpiryDays')}
                  placeholder="365"
                />
                <span className="membership-points-card__helper">Use 0 only if points should never expire for this tier.</span>
                {errors.pointsExpiryDays ? <span className="membership-points-card__error">{errors.pointsExpiryDays}</span> : null}
              </label>

              {errors.points ? <span className="membership-points-card__error">{errors.points}</span> : null}
            </div>

            {/* Points simulator */}
            {earnRate > 0 && redeemRate > 0 ? (
              <div className="membership-points-card__simulator">
                <span className="membership-points-card__simulator-label">Simulator</span>
                <p className="membership-points-card__simulator-text">
                  If a customer spends <strong>₹{monthlySpend.toLocaleString('en-IN')}/month</strong>, they earn{' '}
                  <strong>{monthlyPoints} points</strong> worth <strong>₹{monthlyRedeemValue.toLocaleString('en-IN')}</strong>.
                </p>
              </div>
            ) : null}
          </>
        ) : (
          <div className="membership-points-card__disabledState">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true" style={{ margin: '0 auto' }}>
              <circle cx="32" cy="32" r="18" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" opacity="0.2" />
              <path d="M32 22l3 6.2 6.8 1-4.9 4.8 1.2 6.8L32 37.5l-6.1 3.3 1.2-6.8-4.9-4.8 6.8-1z" fill="currentColor" opacity="0.15" />
            </svg>
            <p className="membership-points-card__disabledTitle">Points are currently off for this membership.</p>
            <p className="membership-points-card__disabledCopy">
              Turn this on if you want the tier to accumulate spend-based rewards and support future redemptions.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default MembershipTypePointsSection;
